import { useRef, type KeyboardEvent } from 'react';
import {
  Building2Icon,
  LoaderIcon,
  LockIcon,
  type LucideIcon,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import {
  VISIBILITY_LINE,
  VISIBILITY_NAME,
} from '@/features/workspaces/accessCopy';
import type { WorkspaceVisibility } from '@/lib/api/generated/model';

interface Option {
  value: WorkspaceVisibility;
  Icon: LucideIcon;
  title: string;
  line: string;
}

const VISIBILITY_OPTIONS: readonly Option[] = [
  {
    value: 'private',
    Icon: LockIcon,
    title: VISIBILITY_NAME.private,
    line: VISIBILITY_LINE.private,
  },
  {
    value: 'organization',
    Icon: Building2Icon,
    title: VISIBILITY_NAME.organization,
    line: VISIBILITY_LINE.organization,
  },
];

/** How many people an option reaches, when the page has read it. */
function reach(
  value: WorkspaceVisibility,
  memberCount: number | undefined,
  organizationCount: number | undefined,
): string | null {
  if (value === 'private') {
    if (memberCount === undefined) return null;
    return memberCount === 1 ? '1 member' : `${String(memberCount)} members`;
  }
  return organizationCount === undefined || organizationCount === 0
    ? 'Everyone in the organization'
    : `Everyone in the organization, ${String(organizationCount)} ${organizationCount === 1 ? 'person' : 'people'}`;
}

export interface VisibilityChoiceProps {
  value: WorkspaceVisibility;
  /** Asks to change; the caller confirms first (spec 193 US2). */
  onChoose: (next: WorkspaceVisibility) => void;
  /** The option being saved, which shows it is on its way. */
  pending: WorkspaceVisibility | null;
  memberCount?: number;
  /** The organization's size, from the directory the people pickers read. */
  organizationCount?: number;
}

/**
 * Who can see the workspace, as two cards side by side (spec 194 US2): what each
 * means and how many people that is, so the choice is made between audiences
 * rather than between two words.
 *
 * A radio group whose arrow keys move between the cards without choosing. The
 * pattern's arrows check as they move; here checking is a confirm dialog about
 * who loses access, and a dialog that opens because focus moved is a trap.
 * Space or Enter on a card asks, as a click does.
 */
export function VisibilityChoice({
  value,
  onChoose,
  pending,
  memberCount,
  organizationCount,
}: VisibilityChoiceProps) {
  const cards = useRef<(HTMLButtonElement | null)[]>([]);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step =
      event.key === 'ArrowRight' || event.key === 'ArrowDown'
        ? 1
        : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
          ? -1
          : 0;
    if (step === 0) return;
    event.preventDefault();
    const from = cards.current.findIndex(
      (card) => card === document.activeElement,
    );
    const at = from === -1 ? 0 : from;
    const next =
      (at + step + VISIBILITY_OPTIONS.length) % VISIBILITY_OPTIONS.length;
    cards.current[next]?.focus();
  };

  return (
    <div
      role="radiogroup"
      aria-label="Who can see it"
      onKeyDown={onKeyDown}
      className="grid gap-3 sm:grid-cols-2"
    >
      {VISIBILITY_OPTIONS.map((option, index) => {
        const current = option.value === value;
        const saving = pending === option.value;
        const count = reach(option.value, memberCount, organizationCount);
        return (
          <button
            key={option.value}
            ref={(element) => {
              cards.current[index] = element;
            }}
            type="button"
            role="radio"
            aria-checked={current}
            aria-busy={saving || undefined}
            tabIndex={current ? 0 : -1}
            disabled={pending !== null}
            onClick={() => {
              if (!current) onChoose(option.value);
            }}
            className={cn(
              'bg-card flex items-start gap-3 rounded-lg border p-4 text-left transition-colors',
              'focus-visible:ring-ring focus-visible:ring-3 focus-visible:outline-none',
              'disabled:cursor-default',
              current
                ? 'border-primary ring-primary ring-1'
                : 'border-border hover:border-foreground/30',
            )}
          >
            <option.Icon
              aria-hidden="true"
              strokeWidth={1.5}
              className={cn(
                'mt-0.5 size-5 shrink-0',
                current ? 'text-primary' : 'text-muted-foreground',
              )}
            />
            <span className="flex min-w-0 flex-col gap-1">
              <span className="flex flex-wrap items-center gap-2">
                <span className="text-foreground text-sm font-medium">
                  {option.title}
                </span>
                {current ? (
                  <span className="border-primary text-primary rounded-full border px-1.5 text-2xs font-medium">
                    Current
                  </span>
                ) : null}
                {saving ? (
                  <span className="text-muted-foreground inline-flex items-center gap-1 text-xs">
                    <LoaderIcon
                      aria-hidden="true"
                      strokeWidth={1.5}
                      className="size-3.5 animate-spin motion-reduce:animate-none"
                    />
                    Saving…
                  </span>
                ) : null}
              </span>
              <span className="text-muted-foreground text-xs">
                {option.line}
              </span>
              {count ? (
                <span className="text-foreground/80 text-xs tabular-nums">
                  {count}
                </span>
              ) : null}
            </span>
          </button>
        );
      })}
    </div>
  );
}
